import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { QueryKey } from '@tanstack/react-query';
import api from '../services/api';
import type { CodeFormatTemplate, PaginatedResponse, Project } from '../types';
import { useOrganizationStore } from '../store/organizationStore';

export interface CreateProjectPayload {
  organization_id: number;
  name: string;
  code: string;
  location?: string;
  client_name?: string;
  deadline?: string;
  budget?: string | null;
  decimal_format?: 'dot' | 'comma' | null;
  date_format?: 'DD.MM.YYYY' | 'YYYY-MM-DD' | 'MM/DD/YYYY' | null;
  code_format_template_id?: number | null;
  /** T1068: 'markup' | 'margin' — omogoča PATCH prek useUpdateProject. */
  margin_calculation_method?: 'markup' | 'margin';
  /** T2733: ISO koda valute projekta (privzeto EUR); cen ne preračuna. */
  currency?: string;
}

export function useProjects(
  filters: Record<string, unknown> = {},
  options: { enabled?: boolean } = {},
) {
  return useQuery<PaginatedResponse<Project>>({
    queryKey: ['projects', filters],
    queryFn: () => api.get('projects/', { params: filters }).then((r) => r.data),
    // T1330: razdelek »Deljeno z mano« se v arhivskem pogledu ne izrisuje,
    // zato ga tam tudi ne poizvedujemo.
    enabled: options.enabled ?? true,
  });
}

/**
 * En projekt po ID-ju.
 *
 * T1474: ista poizvedba je bila do tedaj **prepisana trikrat** — v
 * `BoQEditorPage`, `ProjectDashboardPage` in takratnem `useProjectOrgSync`
 * (T1064; od T2459 ga nasledi `useProjectOrganization`, ki lastne poizvedbe
 * nima in projekt dobi od klicatelja) — vsakič z ročno ponovljenim ključem
 * `['project', id]`. Ključ je moral ostati enak, ker si strani delijo
 * predpomnilnik in ker nanj cilja pet invalidacij: `useUpdateProject`,
 * `useProjectStatus`, `useProjectWebsocket` in `useImportV2`. Vsaka nadaljnja
 * kopija je bila priložnost, da se en od teh ključev tiho razide.
 *
 * Generik nosi razširitve, ki jih vrne isti endpoint (nadzorna plošča bere še
 * `stats`) — brez njega bi klicatelj potreboval svojo poizvedbo.
 *
 * Namerno **brez** `staleTime`: privzetek 0 pomeni, da vsak vstop na zaslon
 * osveži projekt v ozadju, podatek pa je takoj na voljo iz predpomnilnika.
 * Dvig bi upočasnil osvežitev statusne značke in `margin_calculation_method`
 * po urejanju — to je ločena odločitev, ne stranski učinek te združitve.
 */
export function useProject<T extends Project = Project>(
  projectId: number | null | undefined,
) {
  return useQuery<T>({
    queryKey: ['project', projectId],
    queryFn: () => api.get(`projects/${projectId}/`).then((r) => r.data),
    enabled: !!projectId,
  });
}

export function useCreateProject() {
  const qc = useQueryClient();
  const { fetchOrganizations } = useOrganizationStore();
  return useMutation<Project, Error, CreateProjectPayload>({
    mutationFn: (data) => api.post('projects/', data).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['projects'] });
      fetchOrganizations();
    },
  });
}

export function useCodeFormatTemplates(orgSlug: string | undefined) {
  return useQuery<CodeFormatTemplate[]>({
    queryKey: ['code-format-templates', orgSlug],
    queryFn: () =>
      api
        .get(`/organizations/${orgSlug}/code-format-templates/`)
        .then((r) => (Array.isArray(r.data) ? r.data : (r.data.results ?? []))),
    enabled: !!orgSlug,
  });
}

export function useUpdateProject(projectId: number) {
  const qc = useQueryClient();
  return useMutation<Project, Error, Partial<CreateProjectPayload>>({
    mutationFn: (data) => api.patch(`/projects/${projectId}/`, data).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['projects'] });
      qc.invalidateQueries({ queryKey: ['project', projectId] });
      // T2733: KPI »Skupna vrednost« na nadzorni plošči izloči projekte v
      // drugi valuti — brez tega bi do 25 s (`staleTime`) kazal staro stanje.
      qc.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

export function useDeleteProject() {
  const qc = useQueryClient();
  const { fetchOrganizations } = useOrganizationStore();
  return useMutation<void, Error, number>({
    mutationFn: (projectId) => api.delete(`/projects/${projectId}/`).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['projects'] });
      fetchOrganizations();
    },
  });
}

export function useCopyProject() {
  const qc = useQueryClient();
  const { fetchOrganizations } = useOrganizationStore();
  return useMutation<
    Project,
    Error,
    { id: number; name: string; code?: string; reset_prices?: boolean }
  >({
    mutationFn: ({ id, ...data }) =>
      api.post(`/projects/${id}/copy/`, data).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['projects'] });
      fetchOrganizations();
    },
  });
}

export function useToggleFavorite() {
  const qc = useQueryClient();
  return useMutation<
    { is_favorite: boolean },
    Error,
    { id: number; currentValue: boolean },
    { snapshots: [QueryKey, { count: number; results: Project[] } | undefined][] }
  >({
    mutationFn: ({ id, currentValue }) =>
      currentValue
        ? api.delete(`/projects/${id}/favorite/`).then((r) => r.data)
        : api.post(`/projects/${id}/favorite/`).then((r) => r.data),
    onMutate: async ({ id, currentValue }) => {
      await qc.cancelQueries({ queryKey: ['projects'] });
      const snapshots = qc.getQueriesData<{ count: number; results: Project[] }>({
        queryKey: ['projects'],
      });
      qc.setQueriesData<{ count: number; results: Project[] }>(
        { queryKey: ['projects'] },
        (old) => {
          if (!old) return old;
          return {
            ...old,
            results: old.results.map((p) =>
              p.id === id ? { ...p, is_favorite: !currentValue } : p
            ),
          };
        }
      );
      return { snapshots };
    },
    onError: (_err, _vars, context) => {
      if (context?.snapshots) {
        for (const [queryKey, data] of context.snapshots) {
          qc.setQueryData(queryKey, data);
        }
      }
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['projects'] });
    },
  });
}
